import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, CalendarDays, MessageCircle, Plus, X, StickyNote, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import NewOrderDialog from "@/components/NewOrderDialog";
import { listOrders, setNotes, setTags } from "@/lib/api";
import { avatarColor, dayjs, displayPhone, initials, money, ORDER_STATUS, tagColor } from "@/lib/format";

const SUGGESTED = ["nuevo", "interesado", "cliente", "vip", "reclamo", "seguimiento"];

function TagEditor({ conversation, onChange, onRefresh }) {
  const [input, setInput] = useState("");
  const tags = conversation.tags || [];
  const save = async (next) => {
    try { const r = await setTags(conversation.id, next); onChange({ tags: r.tags }); onRefresh(); }
    catch { toast.error("No se pudieron guardar las etiquetas"); }
  };
  const add = (t) => { const v = t.trim().toLowerCase(); if (v && !tags.includes(v)) save([...tags, v]); setInput(""); };
  return (
    <div data-testid="tag-editor">
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <span key={t} className={`inline-flex items-center gap-1 h-6 pl-2.5 pr-1 rounded-full text-xs font-bold ${tagColor(t)}`}>
            {t}<button data-testid={`remove-tag-${t}`} onClick={() => save(tags.filter((x) => x !== t))} className="hover:bg-black/10 rounded-full p-0.5"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); add(input); }} className="mt-2">
        <input data-testid="tag-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Añadir etiqueta y Enter"
          className="w-full h-8 rounded-lg bg-[#F0F2F5] px-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500" />
      </form>
      <div className="mt-2 flex flex-wrap gap-1">
        {SUGGESTED.filter((t) => !tags.includes(t)).map((t) => (
          <button key={t} data-testid={`suggest-tag-${t}`} onClick={() => add(t)} className="text-[11px] px-2 h-5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-emerald-400 hover:text-emerald-700 transition-colors">+ {t}</button>
        ))}
      </div>
    </div>
  );
}

function Notes({ conversation, onChange }) {
  const [v, setV] = useState(conversation.notes || "");
  useEffect(() => { setV(conversation.notes || ""); }, [conversation.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    if (v === (conversation.notes || "")) return;
    await setNotes(conversation.id, v); onChange({ notes: v }); toast.success("Nota guardada");
  };
  return <Textarea data-testid="contact-notes-input" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} rows={3} placeholder="Notas internas (el cliente no las ve)" className="text-sm bg-[#FFFBEA] border-amber-100" />;
}

function Block({ icon: Icon, title, action, children }) {
  return (
    <div className="px-5 py-4 border-b-[8px] border-[#F0F2F5]">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{title}</p>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function ContactProfile({ conversation, messagesCount, onChange, onRefresh }) {
  const c = conversation;
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const loadOrders = () => listOrders({ conversation_id: c.id }).then(setOrders).catch(() => {});
  useEffect(() => { loadOrders(); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <aside data-testid="contact-profile" className="w-[320px] shrink-0 border-l border-slate-200 bg-white h-full overflow-y-auto scroll-thin hidden xl:block">
      <div className="px-5 pt-8 pb-6 text-center border-b-[8px] border-[#F0F2F5]">
        <div className={`h-24 w-24 mx-auto rounded-full text-white text-3xl font-bold flex items-center justify-center ${avatarColor(c.contact_wa_id)}`}>
          {initials(c.contact_name, c.contact_wa_id)}
        </div>
        <h3 className="mt-4 text-xl font-semibold text-[#111B21]" data-testid="contact-name">{c.contact_name || "Sin nombre"}</h3>
        <p className="mt-1 text-sm text-slate-500 flex items-center justify-center gap-1.5"><Phone className="h-3.5 w-3.5" />{displayPhone(c.contact_wa_id)}</p>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-xl bg-[#F0F2F5] py-2"><MessageCircle className="h-3.5 w-3.5 mx-auto text-slate-500" /><p className="mt-1 font-bold" data-testid="contact-messages-count">{messagesCount}</p><p className="text-slate-500">mensajes</p></div>
          <div className="rounded-xl bg-[#F0F2F5] py-2"><CalendarDays className="h-3.5 w-3.5 mx-auto text-slate-500" /><p className="mt-1 font-bold">{dayjs(c.created_at).format("D MMM")}</p><p className="text-slate-500">primer contacto</p></div>
        </div>
      </div>
      <Block icon={StickyNote} title="Etiquetas"><TagEditor conversation={c} onChange={onChange} onRefresh={onRefresh} /></Block>
      <Block icon={ShoppingBag} title="Pedidos" action={
        <Button size="sm" variant="ghost" data-testid="new-order-button" onClick={() => setOpen(true)} className="h-7 text-emerald-700 hover:text-emerald-800"><Plus className="h-3.5 w-3.5 mr-1" />Nuevo</Button>
      }>
        {orders.length === 0 && <p className="text-sm text-slate-400" data-testid="contact-orders-empty">Sin pedidos aún.</p>}
        <ul className="space-y-2">
          {orders.map((o) => (
            <li key={o.id} data-testid={`contact-order-${o.id}`} className="rounded-xl border border-slate-200 p-3">
              <div className="flex justify-between items-center">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${ORDER_STATUS[o.status]?.cls}`}>{ORDER_STATUS[o.status]?.label}</span>
                <span className="font-bold text-sm">{money(o.total)}</span>
              </div>
              <p className="mt-1.5 text-xs text-slate-600">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ") || (o.type === "reserva" ? "Reserva" : "Pedido")}</p>
            </li>
          ))}
        </ul>
      </Block>
      <Block icon={StickyNote} title="Notas"><Notes conversation={c} onChange={onChange} /></Block>
      <NewOrderDialog open={open} onOpenChange={setOpen} conversation={c} onCreated={loadOrders} />
    </aside>
  );
}
